import debugx from "debugx";
// @ts-ignore - webpack will handle this import
import OverpassWorker from "../workers/overpass.worker";

const debug = debugx("bikedash:overpassWorkerService");

interface WorkerMessage {
  type: string;
  id: string;
  data: any;
  debugConfig?: string;
  debugConfigStr?: string;
}

interface WorkerResponse {
  type: string;
  id: string;
  data?: any;
  error?: string;
}

class OverpassWorkerService {
  private static instance: OverpassWorkerService;
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<
    string,
    { resolve: (value: any) => void; reject: (error: any) => void }
  >();

  private constructor() {
    this.initWorker();
  }

  static getInstance(): OverpassWorkerService {
    if (!OverpassWorkerService.instance) {
      OverpassWorkerService.instance = new OverpassWorkerService();
    }
    return OverpassWorkerService.instance;
  }

  private initWorker() {
    try {
      // @ts-ignore
      this.worker = new OverpassWorker();
      this.setupWorkerListeners();
      debug(
        "Overpass Worker created - heavy tile operations will run in background thread",
      );

      // Send initial API config to worker
      const storedConfig = localStorage.getItem("overpassApiConfig");
      if (storedConfig) {
        try {
          const config = JSON.parse(storedConfig);
          this.sendMessage("updateApiConfig", config).catch((error) => {
            debug.warn("Failed to send initial API config to worker:", error);
          });
          debug("Sent initial API config to worker");
        } catch (error) {
          debug.warn("Failed to parse stored API config:", error);
        }
      }
    } catch (error) {
      debug.error("Failed to create worker:", error);
      this.worker = null;
    }
  }

  private setupWorkerListeners() {
    if (!this.worker) return;

    this.worker.addEventListener(
      "message",
      (event: MessageEvent<WorkerResponse>) => {
        const { type, id, data, error } = event.data;

        // Handle cache ready notification
        if (type === "cacheReady") {
          debug(
            "Cache ready notification from worker: %d tiles indexed",
            data.count,
          );
          window.dispatchEvent(
            new CustomEvent("cacheUpdated", { detail: data }),
          );
          return;
        }

        // Handle cache update notifications
        if (type === "cacheUpdated") {
          debug(
            "Cache updated notification from worker: grid %.1f, %.1f",
            data.gridLat,
            data.gridLon,
          );
          window.dispatchEvent(
            new CustomEvent("cacheUpdated", { detail: data }),
          );
          return;
        }

        // Handle API state change notifications
        if (type === "apiStateChanged") {
          debug(
            "API state changed: suspended=%s, waiting=%s",
            data.suspended,
            data.currentlyWaiting,
          );
          window.dispatchEvent(
            new CustomEvent("overpassApiStateChanged", { detail: data }),
          );
          return;
        }

        const pending = this.pendingMessages.get(id);

        if (pending) {
          if (error) {
            pending.reject(new Error(error));
          } else {
            pending.resolve(data);
          }
          this.pendingMessages.delete(id);
        }
      },
    );

    this.worker.addEventListener("error", (error) => {
      debug.error("Worker error:", error);
      for (const [, pending] of this.pendingMessages) {
        pending.reject(new Error("Worker crashed"));
      }
      this.pendingMessages.clear();
      this.restartWorker();
    });
  }

  private restartWorker() {
    debug("Attempting to restart worker...");
    this.worker = null;
    setTimeout(() => {
      this.initWorker();
    }, 1000);
  }

  sendMessage<T>(type: string, data: any): Promise<T> {
    if (!this.worker) {
      throw new Error("Worker not available");
    }

    return new Promise((resolve, reject) => {
      const id = `msg_${++this.messageId}`;

      this.pendingMessages.set(id, { resolve, reject });

      const message: WorkerMessage = {
        type,
        id,
        data,
        debugConfig:
          typeof localStorage !== "undefined"
            ? localStorage.getItem("debug") || ""
            : "",
        debugConfigStr:
          typeof localStorage !== "undefined"
            ? localStorage.getItem("debugConfig") || ""
            : "",
      };
      this.worker!.postMessage(message);

      // Timeout after 60 seconds (longer for heavy operations)
      setTimeout(() => {
        if (this.pendingMessages.has(id)) {
          this.pendingMessages.delete(id);
          reject(new Error(`Worker timeout for message ${type}`));
        }
      }, 60000);
    });
  }

  async searchLocalBikeFeatures(
    lat: number,
    lon: number,
    gridRange: number = 3,
    onlyCached: boolean = false,
    trailsOnly: boolean = false,
  ): Promise<any[]> {
    try {
      return await this.sendMessage("searchLocalBikeFeatures", {
        lat,
        lon,
        gridRange,
        onlyCached,
        trailsOnly,
      });
    } catch (error) {
      debug.warn("Worker failed for local bike features:", error);
      throw error;
    }
  }

  async getCacheStats(): Promise<{
    count: number;
    oldestDate: Date | null;
    newestDate: Date | null;
    gridSize: number;
    paddingMiles: number;
    totalSize: number;
  }> {
    try {
      const stats = await this.sendMessage<any>("getCacheStats", {});
      return {
        ...stats,
        oldestDate: stats.oldestDate ? new Date(stats.oldestDate) : null,
        newestDate: stats.newestDate ? new Date(stats.newestDate) : null,
      };
    } catch (error) {
      debug.warn("Worker failed to get cache stats:", error);
      throw error;
    }
  }

  async clearCache(): Promise<boolean> {
    try {
      return await this.sendMessage("clearCache", {});
    } catch (error) {
      debug.warn("Worker failed to clear cache:", error);
      throw error;
    }
  }

  async checkGridStatus(
    gridLat: number,
    gridLon: number,
  ): Promise<"cached" | "failed" | "fetching" | "none"> {
    try {
      return await this.sendMessage("checkGridStatus", { gridLat, gridLon });
    } catch (error) {
      debug.warn("Worker failed to check grid status:", error);
      return "none";
    }
  }

  async checkGridStatusBatch(
    tiles: Array<{ gridLat: number; gridLon: number }>,
  ): Promise<Array<"cached" | "failed" | "fetching" | "none">> {
    try {
      return await this.sendMessage("checkGridStatusBatch", { tiles });
    } catch (error) {
      debug.warn("Worker failed to check grid status batch:", error);
      return tiles.map(() => "none");
    }
  }

  async getApiState(): Promise<{
    suspended: boolean;
    suspendedAt: number | null;
    suspendReason: string | null;
    manualSuspension: boolean;
    consecutive429Count: number;
    total429Count: number;
    total504Count: number;
    totalRequests: number;
    totalErrors: number;
    lastRequestTime: number;
    nextRequestAvailableAt: number;
    currentlyWaiting: boolean;
    waitingUntil: number | null;
    waitReason: string | null;
  }> {
    try {
      return await this.sendMessage("getApiState", {});
    } catch (error) {
      debug.warn("Worker failed to get API state:", error);
      throw error;
    }
  }

  async resetApiSuspension(): Promise<any> {
    try {
      return await this.sendMessage("resetApiSuspension", {});
    } catch (error) {
      debug.warn("Worker failed to reset API suspension:", error);
      throw error;
    }
  }

  async toggleManualSuspension(): Promise<any> {
    try {
      return await this.sendMessage("toggleManualSuspension", {});
    } catch (error) {
      debug.warn("Worker failed to toggle manual suspension:", error);
      throw error;
    }
  }

  async updateApiConfig(config: any): Promise<any> {
    try {
      return await this.sendMessage("updateApiConfig", config);
    } catch (error) {
      debug.warn("Worker failed to update API config:", error);
      throw error;
    }
  }

  destroy() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingMessages.clear();
  }
}

export function getOverpassWorkerService(): OverpassWorkerService {
  return OverpassWorkerService.getInstance();
}
